/**
 * CapabilitySettingsForm — renderer dinámico de settings desde `config_schema`
 * (REQ-CAP-002 §4: la consola renderiza automáticamente; sin código por capacidad).
 *
 * Soporta el subconjunto del schema que exponen las capacidades:
 * string | integer | number | boolean | array (de strings) | enum.
 * Las claves sin soporte específico se muestran como texto.
 */

import { Card, Input } from '@/components/ui';

import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import type { CapabilityConfigSchema, CapabilitySchemaProperty, SettingsValues } from './types';

interface Props {
  schema: CapabilityConfigSchema | null | undefined;
  values: SettingsValues;
  onChange?: (values: SettingsValues) => void;
  disabled?: boolean;
}

function propertyValue(
  spec: CapabilitySchemaProperty,
  values: SettingsValues,
  key: string,
): unknown {
  return values[key] !== undefined ? values[key] : spec.default;
}

function renderControl(
  key: string,
  spec: CapabilitySchemaProperty,
  values: SettingsValues,
  onChange: ((values: SettingsValues) => void) | undefined,
  disabled: boolean,
) {
  const value = propertyValue(spec, values, key);
  const type = spec.type ?? 'string';

  if (Array.isArray(spec.enum) && spec.enum.length > 0) {
    return (
      <select
        id={`capability-setting-${key}`}
        className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm dark:border-gray-600 dark:bg-gray-900"
        value={typeof value === 'string' ? value : ''}
        disabled={disabled || !onChange}
        onChange={(event) =>
          onChange?.({ ...values, [key]: event.target.value === '' ? null : event.target.value })
        }
      >
        <option value="">—</option>
        {spec.enum.map((option) => (
          <option key={String(option)} value={String(option)}>
            {String(option)}
          </option>
        ))}
      </select>
    );
  }

  if (type === 'boolean') {
    return (
      <Checkbox
        id={`capability-setting-${key}`}
        checked={value === true}
        disabled={disabled || !onChange}
        onCheckedChange={(checked) => onChange?.({ ...values, [key]: checked === true })}
      />
    );
  }

  if (type === 'array') {
    const text = Array.isArray(value) ? (value as unknown[]).join(', ') : '';
    return (
      <Input
        id={`capability-setting-${key}`}
        value={text}
        disabled={disabled || !onChange}
        placeholder="a, b, c"
        onChange={(event) =>
          onChange?.({
            ...values,
            [key]: event.target.value
              .split(',')
              .map((item) => item.trim())
              .filter(Boolean),
          })
        }
      />
    );
  }

  if (type === 'integer' || type === 'number') {
    return (
      <Input
        id={`capability-setting-${key}`}
        type="number"
        value={value === undefined || value === null ? '' : String(value)}
        disabled={disabled || !onChange}
        onChange={(event) =>
          onChange?.({
            ...values,
            [key]: event.target.value === '' ? null : Number(event.target.value),
          })
        }
      />
    );
  }

  return (
    <Input
      id={`capability-setting-${key}`}
      value={value === undefined || value === null ? '' : String(value)}
      disabled={disabled || !onChange}
      onChange={(event) => onChange?.({ ...values, [key]: event.target.value })}
    />
  );
}

export function CapabilitySettingsForm({ schema, values, onChange, disabled = false }: Props) {
  const properties = schema?.properties ?? {};
  const keys = Object.keys(properties);
  if (keys.length === 0) {
    return null;
  }

  return (
    <Card className="p-4 space-y-4">
      {keys.map((key) => {
        const spec = properties[key];
        const type = spec.type ?? 'string';
        const isBoolean = type === 'boolean';
        return (
          <div
            key={key}
            className={isBoolean ? 'flex items-center gap-3' : 'space-y-1.5'}
          >
            {isBoolean ? (
              <>
                {renderControl(key, spec, values, onChange, disabled)}
                <Label htmlFor={`capability-setting-${key}`}>
                  {spec.title || key}
                  {spec.required ? ' *' : ''}
                </Label>
              </>
            ) : (
              <>
                <Label htmlFor={`capability-setting-${key}`}>
                  {spec.title || key}
                  {spec.required ? ' *' : ''}
                </Label>
                {renderControl(key, spec, values, onChange, disabled)}
              </>
            )}
            {spec.description && (
              <p className="text-xs text-gray-500 dark:text-gray-400">{spec.description}</p>
            )}
          </div>
        );
      })}
    </Card>
  );
}
